import { createContext, useContext, useState } from "react";

export const STRINGS = {
  en: {
    tag: "Miami · Photographer",
    nav: { work: "Work", story: "Story", services: "Services", book: "Book", cta: "Book a session" },
    hero: {
      overline: "Freelance Photographer · EN / PT / ES",
      l1: "Stories", l2a: "worth", l2b: "a", l3: "thousand", l4: "frames.",
      desc: "I'm Lilah Chavarria — a Miami-based photographer capturing weddings, families, brands and everything in between with a warm, editorial eye.",
      cta: "Book a session", fig: "Fig. 01 — Coral Gables wedding", scroll: "Scroll to explore ↓",
    },
    story: {
      overline: "Why Photography", behind: "Behind the lens — Lilah Chavarria",
      chapters: [
        { title: "The Spark", body: "From a young age I was captivated by my father's camera — fascinated by the quiet ritual of capturing a moment and shaping it in the edit. That fascination never left." },
        { title: "The Story", body: "A picture is worth a thousand words. Every frame I make is built to carry a story — one that resonates long after the shutter closes." },
        { title: "The Promise", body: "My style is focused on your goals. I listen first, then I deliver exactly what you need — nothing generic, everything intentional." },
      ],
    },
    work: { overline: "Selected Work", t1: "The", t2: "portfolio",
      cats: { All: "All", Weddings: "Weddings", DJ: "DJ" } },
    services: { overline: "What I Offer", t1: "Services,", t2: "tailored", t3: "to your goals." },
    booking: {
      overline: "Booking", t1: "Let's create", t2: "something",
      desc: "Pick a date, tell me about your vision, and I'll reach out to confirm the details. Every session is shaped around your story.",
      step1: "01 — Choose a service", step2: "02 — Preferred date", step3: "03 — Your details",
      name: "Full name", email: "Email", phone: "Phone (optional)", msg: "Tell me about your vision…",
      submit: "Send booking request", sending: "Sending…",
      successTitle: "Request received.", successBody: (n, s, d, e) => `Thank you, ${n}. Your ${s} request for ${d} is in. I'll be in touch at ${e} soon.`,
      again: "Make another request",
      errDate: "Please choose a preferred date.", errService: "Please select a service.", errName: "Name and email are required.",
      ok: "Request sent — Lilah will be in touch shortly.", fail: "Something went wrong. Please try again.",
    },
    footer: { overline: "Get in touch", t1: "Let's make", t2: "memories.", book: "Book a session", loc: "Miami, FL" },
  },
  pt: {
    tag: "Miami · Fotógrafa",
    nav: { work: "Trabalhos", story: "História", services: "Serviços", book: "Reservar", cta: "Agendar sessão" },
    hero: {
      overline: "Fotógrafa Freelancer · EN / PT / ES",
      l1: "Histórias", l2a: "que valem", l2b: "", l3: "mais que", l4: "mil palavras.",
      desc: "Sou Lilah Chavarria — fotógrafa em Miami, registrando casamentos, famílias, marcas e tudo mais com um olhar caloroso e editorial.",
      cta: "Agendar sessão", fig: "Fig. 01 — Casamento em Coral Gables", scroll: "Role para explorar ↓",
    },
    story: {
      overline: "Por que fotografia", behind: "Por trás das lentes — Lilah Chavarria",
      chapters: [
        { title: "A Faísca", body: "Desde cedo fui fascinada pela câmera do meu pai — encantada pelo ritual silencioso de capturar um momento e moldá-lo na edição. Esse fascínio nunca me deixou." },
        { title: "A História", body: "Uma imagem vale mais que mil palavras. Cada foto que faço é criada para contar uma história — que ressoa muito depois do clique." },
        { title: "A Promessa", body: "Meu estilo é focado nos seus objetivos. Primeiro eu escuto, depois entrego exatamente o que você precisa — nada genérico, tudo intencional." },
      ],
    },
    work: { overline: "Trabalhos Selecionados", t1: "O", t2: "portfólio",
      cats: { All: "Todos", Weddings: "Casamentos", DJ: "DJ" } },
    services: { overline: "O Que Ofereço", t1: "Serviços,", t2: "sob medida", t3: "para seus objetivos." },
    booking: {
      overline: "Reservas", t1: "Vamos criar", t2: "algo",
      desc: "Escolha uma data, conte-me sua visão, e entrarei em contato para confirmar os detalhes. Cada sessão é moldada à sua história.",
      step1: "01 — Escolha um serviço", step2: "02 — Data preferida", step3: "03 — Seus dados",
      name: "Nome completo", email: "E-mail", phone: "Telefone (opcional)", msg: "Conte-me sua visão…",
      submit: "Enviar pedido de reserva", sending: "Enviando…",
      successTitle: "Pedido recebido.", successBody: (n, s, d, e) => `Obrigada, ${n}. Seu pedido de ${s} para ${d} foi enviado. Entrarei em contato em ${e} em breve.`,
      again: "Fazer outro pedido",
      errDate: "Escolha uma data preferida.", errService: "Selecione um serviço.", errName: "Nome e e-mail são obrigatórios.",
      ok: "Pedido enviado — Lilah entrará em contato em breve.", fail: "Algo deu errado. Tente novamente.",
    },
    footer: { overline: "Entre em contato", t1: "Vamos criar", t2: "memórias.", book: "Agendar sessão", loc: "Miami, FL" },
  },
  es: {
    tag: "Miami · Fotógrafa",
    nav: { work: "Trabajos", story: "Historia", services: "Servicios", book: "Reservar", cta: "Reservar sesión" },
    hero: {
      overline: "Fotógrafa Freelance · EN / PT / ES",
      l1: "Historias", l2a: "que valen", l2b: "", l3: "más que", l4: "mil palabras.",
      desc: "Soy Lilah Chavarria — fotógrafa en Miami, capturando bodas, familias, marcas y todo lo demás con una mirada cálida y editorial.",
      cta: "Reservar sesión", fig: "Fig. 01 — Boda en Coral Gables", scroll: "Desplázate para explorar ↓",
    },
    story: {
      overline: "Por qué fotografía", behind: "Detrás del lente — Lilah Chavarria",
      chapters: [
        { title: "La Chispa", body: "Desde muy joven me cautivó la cámara de mi padre — fascinada por el ritual silencioso de capturar un momento y darle forma en la edición. Esa fascinación nunca se fue." },
        { title: "La Historia", body: "Una imagen vale más que mil palabras. Cada foto que hago está pensada para contar una historia — una que resuena mucho después del clic." },
        { title: "La Promesa", body: "Mi estilo se enfoca en tus objetivos. Primero escucho, luego entrego exactamente lo que necesitas — nada genérico, todo intencional." },
      ],
    },
    work: { overline: "Trabajos Seleccionados", t1: "El", t2: "portafolio",
      cats: { All: "Todos", Weddings: "Bodas", DJ: "DJ" } },
    services: { overline: "Lo Que Ofrezco", t1: "Servicios,", t2: "a tu medida", t3: "según tus objetivos." },
    booking: {
      overline: "Reservas", t1: "Creemos", t2: "algo",
      desc: "Elige una fecha, cuéntame tu visión, y me pondré en contacto para confirmar los detalles. Cada sesión se adapta a tu historia.",
      step1: "01 — Elige un servicio", step2: "02 — Fecha preferida", step3: "03 — Tus datos",
      name: "Nombre completo", email: "Correo", phone: "Teléfono (opcional)", msg: "Cuéntame tu visión…",
      submit: "Enviar solicitud de reserva", sending: "Enviando…",
      successTitle: "Solicitud recibida.", successBody: (n, s, d, e) => `Gracias, ${n}. Tu solicitud de ${s} para ${d} está enviada. Me pondré en contacto en ${e} pronto.`,
      again: "Hacer otra solicitud",
      errDate: "Elige una fecha preferida.", errService: "Selecciona un servicio.", errName: "Nombre y correo son obligatorios.",
      ok: "Solicitud enviada — Lilah te contactará pronto.", fail: "Algo salió mal. Inténtalo de nuevo.",
    },
    footer: { overline: "Contáctame", t1: "Creemos", t2: "recuerdos.", book: "Reservar sesión", loc: "Miami, FL" },
  },
};

const LangCtx = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  const t = STRINGS[lang];
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);
